import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RawIntlProvider } from 'react-intl';
import Snackbar from '@mui/material/Snackbar';
import { Array as A, Option, pipe } from 'effect';

import { organizationNameSelector } from '@/domain/env/envSelector';
import { countMarkedPlaces } from '@/lib/i18nOverrides/domScan';
import { getEditMode, setEditMode, toggleEditMode, useEditMode } from '@/lib/i18nOverrides/editMode';
import { hasMarker, stripMarkers } from '@/lib/i18nOverrides/markers';
import { markerRegistry } from '@/lib/i18nOverrides/registry';
import EditModeControl from '@/components/TextOverrides/EditModeControl';
import { EDITOR_UI_ATTRIBUTE, isInsideEditorUi } from '@/components/TextOverrides/editorUi';
import PencilOverlay from '@/components/TextOverrides/PencilOverlay';
import TextOverrideDialog, { type DialogTarget } from '@/components/TextOverrides/TextOverrideDialog';
import { useTextOverridesContext } from '@/components/TextOverrides/TextOverridesContext';

const isToggleShortcut = (e: KeyboardEvent) => (e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyE';

// The app stays usable in edit mode, so Esc first belongs to an open app menu or dialog.
const isAppModalOpen = () =>
  Array.from(document.querySelectorAll('.MuiModal-root:not(.MuiModal-hidden)')).some((m) => !isInsideEditorUi(m));

const toTargets = (indexes: ReadonlyArray<number>): ReadonlyArray<DialogTarget> =>
  pipe(
    indexes,
    A.filterMap((i) => markerRegistry.get(i)),
    A.dedupeWith((a, b) => a.id === b.id),
    A.map((entry) => ({
      entry,
      usedIn: countMarkedPlaces(document.body, (i) =>
        pipe(
          markerRegistry.get(i),
          Option.exists((e) => e.id === entry.id),
        ),
      ),
    })),
  );

/**
 * Text edit mode UI: the always-visible control (POC), shortcut, pencil overlay, editor dialog, copy and title guards.
 * Mounted once inside the theme; renders under the unmarked intl so the editor never marks its own texts. In normal
 * mode only the control is rendered — no listener, observer or scan runs.
 */
const TextOverridesEditor = () => {
  const { plainIntl, orgId } = useTextOverridesContext();
  const organizationName: unknown = useSelector(organizationNameSelector);
  const editMode = useEditMode();
  const [targets, setTargets] = useState<ReadonlyArray<DialogTarget> | null>(null);
  // The message outlives `open` so the closing fade does not show an empty bar.
  const [notice, setNoticeState] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const setNotice = useCallback((message: string) => setNoticeState({ open: true, message }), []);

  // Ctrl/Cmd+Shift+E toggles; Esc leaves, unless the editor dialog or an app menu/dialog is open (it closes first).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isToggleShortcut(e) && orgId !== null) {
        e.preventDefault();
        e.stopPropagation();
        toggleEditMode();
      } else if (e.key === 'Escape' && getEditMode() && targets === null && !isAppModalOpen()) {
        setEditMode(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [orgId, targets]);

  // Leaving the org (sign-out, org switch to none) leaves edit mode.
  useEffect(() => {
    if (orgId === null) setEditMode(false);
  }, [orgId]);

  useEffect(() => {
    if (!editMode) setTargets(null);
  }, [editMode]);

  // Copied text must not carry markers into Excel, emails or other apps.
  useEffect(() => {
    if (!editMode) return undefined;
    const onCopy = (e: ClipboardEvent) => {
      const selected = window.getSelection()?.toString() ?? '';
      if (!hasMarker(selected) || !e.clipboardData) return;
      e.clipboardData.setData('text/plain', stripMarkers(selected));
      e.preventDefault();
    };
    document.addEventListener('copy', onCopy, true);
    return () => document.removeEventListener('copy', onCopy, true);
  }, [editMode]);

  // The tab title is not pickable, and a marked title would leak into history entries and bookmarks.
  useEffect(() => {
    if (!editMode) return undefined;
    const strip = () => {
      if (hasMarker(document.title)) document.title = stripMarkers(document.title);
    };
    strip();
    const observer = new MutationObserver(strip);
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  }, [editMode]);

  const onOpen = useCallback(
    (indexes: ReadonlyArray<number>) => {
      const found = toTargets(indexes);
      if (found.length === 0) {
        // The registry was cleared since the text was marked (edit mode toggled): the page re-renders with new marks.
        setNotice('This text changed meanwhile — hover it again and use its new pencil.');
        return;
      }
      setTargets(found);
    },
    [setNotice],
  );

  if (orgId === null) return null;

  return (
    <RawIntlProvider value={plainIntl}>
      <EditModeControl
        editMode={editMode}
        organizationName={typeof organizationName === 'string' && organizationName ? organizationName : orgId}
        onToggle={toggleEditMode}
      />
      {/* Unmounted while the dialog is open: no pencils or outline over the dialog. */}
      {editMode && targets === null && <PencilOverlay onOpen={onOpen} />}
      {editMode && targets && (
        <TextOverrideDialog
          key={targets.map((t) => t.entry.id).join('|')}
          orgId={orgId}
          targets={targets}
          onClose={() => setTargets(null)}
          onSaved={(message) => {
            setTargets(null);
            setNotice(message);
          }}
        />
      )}
      <Snackbar
        open={notice.open}
        message={notice.message}
        autoHideDuration={3500}
        onClose={() => setNoticeState((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 'textOverridesEditor', bottom: { xs: 72, sm: 72 } }}
        {...{ [EDITOR_UI_ATTRIBUTE]: '' }}
      />
    </RawIntlProvider>
  );
};

export default TextOverridesEditor;
